import {
  DefaultError,
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { displayToastError, displayToastSuccess } from '@/helpers';
import { invalidateIntegrationStatusRelatedQueries } from '@/modules/common';
import { configureClockifyIntegration } from '@/modules/onboarding/integrations-setup/api/clockify-integration.api';
import { ConfigureApiTokenIntegrationParams } from '@/modules/onboarding/integrations-setup/types/api-token/configure-api-token-integration-params';
import { UseConfigureApiTokenIntegrationParams } from '@/modules/onboarding/integrations-setup/types/api-token/use-configure-api-token-integration-params';

/**
 * Hook to handle Clockify integration configuration
 * Configures Clockify integration with API token and invalidates integration status on success
 */
export const useConfigureClockifyIntegration = ({
  onSuccess,
  onError,
}: UseConfigureApiTokenIntegrationParams = {}): UseMutationResult<
  unknown,
  DefaultError,
  ConfigureApiTokenIntegrationParams
> => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(['authenticated.onboarding.integrations']);

  return useMutation({
    mutationFn: async (data: ConfigureApiTokenIntegrationParams) => {
      await configureClockifyIntegration({ apiKey: data.apiToken });
    },
    onSuccess: async () => {
      // Invalidate integration status to refetch latest data
      await invalidateIntegrationStatusRelatedQueries(queryClient);

      // Show success toast
      displayToastSuccess(t('toast.integrationSuccess'));

      onSuccess?.();
    },
    onError: (error: Error) => {
      // Show error toast
      displayToastError(error.message);
      onError?.(error);
    },
  });
};
